<template>
  <div class="archive-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">Archive</h1>
        <p class="page-sub">All archived tasks — nothing is permanently deleted</p>
      </div>
    </div>

    <div class="arch-tabs">
      <button class="tab-btn" :class="{ active: tab === 'tasks' }" @click="tab = 'tasks'; loadTasks()">Tasks</button>
      <button class="tab-btn" :class="{ active: tab === 'projects' }" @click="tab = 'projects'; loadProjects()">Projects</button>
    </div>

    <!-- ── Archived Tasks ── -->
    <template v-if="tab === 'tasks'">
      <div v-if="tasksLoading" class="spinner"></div>
      <div v-else-if="tasks.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        <p>No archived tasks.</p>
      </div>
      <div v-else class="arch-list">
        <div v-for="t in tasks" :key="t.id" class="arch-item">
          <div class="arch-stripe" :style="{ background: urgencyColor(t.urgency) }"></div>
          <div class="arch-body">
            <div class="arch-title">{{ t.title }}</div>
            <div class="arch-meta">
              <span v-if="t.project_name" class="arch-tag" :style="{ color: t.project_color }">{{ t.project_name }}</span>
              <span class="arch-badge" :class="'badge-' + t.urgency">{{ t.urgency }}</span>
              <span class="arch-badge" :class="'badge-' + t.status">{{ t.status }}</span>
              <span v-if="t.archived_at" class="arch-date">Archived {{ formatDate(t.archived_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Archived Projects ── -->
    <template v-if="tab === 'projects'">
      <div v-if="projsLoading" class="spinner"></div>
      <div v-else-if="projs.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        <p>No archived projects.</p>
      </div>
      <div v-else class="arch-list">
        <div v-for="p in projs" :key="p.id" class="arch-item">
          <div class="arch-stripe" :style="{ background: p.color }"></div>
          <div class="arch-body">
            <div class="arch-title">{{ p.name }}</div>
            <div class="arch-meta">
              <span v-if="p.description" class="arch-desc">{{ p.description }}</span>
              <span class="arch-date">Archived {{ formatDate(p.archived_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/api'

const URGENCY_COLORS = { low: '#64748b', normal: '#6366f1', high: '#f59e0b', critical: '#ef4444' }

export default {
  name: 'ArchiveView',
  setup() {
    const tab         = ref('tasks')
    const tasks       = ref([])
    const projs       = ref([])
    const tasksLoading = ref(true)
    const projsLoading = ref(false)

    async function loadTasks() {
      tasksLoading.value = true
      try { tasks.value = (await api.getTasks({ archived: 1 })).data || [] } catch {} finally { tasksLoading.value = false }
    }

    async function loadProjects() {
      projsLoading.value = true
      try { projs.value = (await api.getProjects({ archived: 1 })).data || [] } catch {} finally { projsLoading.value = false }
    }

    const urgencyColor = (u) => URGENCY_COLORS[u] || '#6366f1'

    function formatDate(dt) {
      if (!dt) return ''
      return new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    onMounted(loadTasks)
    return { tab, tasks, projs, tasksLoading, projsLoading, loadTasks, loadProjects, urgencyColor, formatDate }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.arch-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
}
.tab-btn {
  padding: 6px 18px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: none;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
}
.tab-btn:hover  { border-color: var(--primary); color: var(--primary); }
.tab-btn.active { background: var(--primary); border-color: var(--primary); color: #fff; }

.arch-list { display: flex; flex-direction: column; gap: 8px; }

.arch-item {
  display: flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  opacity: .75;
}
.arch-stripe { width: 4px; flex-shrink: 0; }
.arch-body { flex: 1; padding: 12px 16px; }
.arch-title { font-size: 14px; font-weight: 600; margin-bottom: 6px; }
.arch-meta  { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.arch-tag   { font-size: 11px; font-weight: 600; }
.arch-desc  { font-size: 12px; color: var(--muted); }
.arch-date  { font-size: 11px; color: var(--muted); margin-left: auto; }
.arch-badge {
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 20px;
  font-weight: 500;
  background: rgba(255,255,255,.07);
  color: var(--muted);
}
</style>
